import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import { ReactNode } from 'react';
import { colors } from '../../theme/colors';
import LoginActivityGraph from './login-activity-graph';
import {
  FULL_WIDTH_TILE_COLUMNS,
  HALF_WIDTH_TILE_COLUMNS,
  SECONDARY_STAT_LABEL_VALUE_GAP,
  SECONDARY_STAT_PADDING,
  STATS_ROW_GAP_FLOOR,
  TILE_CONTENT_PADDING,
  WIDE_TILE_REGIONS_GAP,
  wideTileRegionSx,
} from './tile-config';
import type { CommandDashboardTileBodyProps } from './tile-content-registry';
import TileInsufficientAccessNotice from './tile-insufficient-access-notice';
import { useCanSeeTile } from './use-tile-access';
import { useLoginActivityTileMetrics } from './use-login-activity-tile-metrics';
import { useBindTileRefresh } from './use-tile-refresh';

import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import HighlightOff from '@mui/icons-material/HighlightOff';
import WarningAmber from '@mui/icons-material/WarningAmber';
import type SvgIcon from '@mui/material/SvgIcon';

type SvgIconComponent = typeof SvgIcon;

type LayoutTier = 'wide' | 'compact' | 'narrow';

function layoutTierFor(columns: number): LayoutTier {
  if (columns >= FULL_WIDTH_TILE_COLUMNS) {
    return 'wide';
  }
  return columns >= HALF_WIDTH_TILE_COLUMNS ? 'compact' : 'narrow';
}

interface InfoCardProps {
  icon: SvgIconComponent;
  label: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
}

function InfoCard({
  icon: Icon,
  label,
  bgColor,
  borderColor,
  textColor,
}: InfoCardProps) {
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'flex-start',
        boxSizing: 'border-box',
        padding: '8px 12px',
        borderRadius: '4px',
        bgcolor: bgColor,
        border: `0.5px solid ${borderColor}`,
      }}
    >
      <Icon sx={{ width: 24, height: 24, color: textColor, flexShrink: 0 }} />

      <Typography
        variant="body2"
        sx={{
          color: textColor,
          minWidth: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

interface SecondaryStatProps {
  label: string;
  value: ReactNode;
  tier: LayoutTier;
}

function SecondaryStat({ label, value, tier }: SecondaryStatProps) {
  return (
    <Stack
      sx={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: '16px 8px',
        borderBottom: (theme) =>
          `0.5px solid ${theme.palette.semantic.outline.light}`,
        ...(tier !== 'narrow' && {
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: `${SECONDARY_STAT_LABEL_VALUE_GAP}px`,
          flex: 1,
          minWidth: 0,
          width: 'auto',
          padding: SECONDARY_STAT_PADDING,
          border: 'none',
        }),
      }}
    >
      <Typography
        variant="body1"
        sx={{
          color: (theme) => theme.palette.semantic.text.title2,
          ...(tier !== 'narrow' && {
            width: '100%',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }),
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          typography: tier === 'narrow' ? 'h5' : 'h4',
          color: (theme) => theme.palette.semantic.stepper.active,
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default function LoginActivityTileMetrics({
  tile,
  columns = FULL_WIDTH_TILE_COLUMNS,
}: CommandDashboardTileBodyProps) {
  const tier = layoutTierFor(columns);
  const theme = useTheme();
  const failedLoginsTextColor =
    theme.palette.mode === 'dark'
      ? colors.error[700]
      : theme.palette.semantic.text.error2;
  const hasPermission = useCanSeeTile(tile);
  const {
    snapshot,
    isLoading,
    isRefreshing,
    onRefresh,
    totalLogins7d,
    failedLoginsToday,
    leakedPassword7d,
    suspiciousBlocked7d,
    genericDisable7d,
    lastUpdated,
    truncated,
  } = useLoginActivityTileMetrics(hasPermission);
  useBindTileRefresh(tile, hasPermission, {
    onRefresh,
    isFetching: isRefreshing,
  });

  if (!hasPermission && snapshot === undefined) {
    return <TileInsufficientAccessNotice tile={tile} />;
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          boxSizing: 'border-box',
          justifyContent: 'space-between',
          padding: `${TILE_CONTENT_PADDING}px`,
          gap: tier === 'narrow' ? 0 : `${STATS_ROW_GAP_FLOOR}px`,
          ...(tier === 'wide' && {
            flexDirection: 'row',
            alignItems: 'center',
            gap: `${WIDE_TILE_REGIONS_GAP}px`,
          }),
        }}
      >
        <Stack
          sx={{
            ...(tier === 'narrow'
              ? {
                  display: 'contents',
                }
              : {
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 0,
                }),
            ...(tier === 'wide' && wideTileRegionSx),
          }}
        >
          <Stack
            sx={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '16px',
              width: '100%',
              ...(tier !== 'narrow' && {
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'space-between',
                width: 255,
                alignSelf: 'stretch',
                flexShrink: 0,
                ml: '16px',
              }),
            }}
          >
            <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body1"
                sx={{ color: (theme) => theme.palette.semantic.text.title2 }}
              >
                Total Workspace Logins
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 400,
                  color: (theme) => theme.palette.semantic.stepper.active,
                }}
              >
                {isLoading ? (
                  <Skeleton width={100} />
                ) : (
                  totalLogins7d.toLocaleString()
                )}
              </Typography>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ minWidth: 0 }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: (theme) => theme.palette.semantic.text.support,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                  }}
                >
                  {isLoading ? (
                    <Skeleton width={140} />
                  ) : lastUpdated ? (
                    `Last Updated: ${dayjs(lastUpdated * 1000).format(
                      'MMM D, YYYY, h:mm A'
                    )}`
                  ) : (
                    'Last Updated: —'
                  )}
                </Typography>
                {truncated && (
                  <Tooltip
                    title="Some login data may be incomplete for this period due to high activity volume. A later refresh will self-correct."
                    arrow
                  >
                    <WarningAmber
                      fontSize="small"
                      sx={{
                        color: (theme) =>
                          theme.palette.semantic.metricText.warning,
                      }}
                    />
                  </Tooltip>
                )}
              </Stack>
            </Stack>
            {isLoading ? (
              <Stack spacing={1.25} sx={{ width: 206, flexShrink: 0 }}>
                <Skeleton variant="rounded" width="100%" height={40} />
                <Skeleton variant="rounded" width="100%" height={40} />
              </Stack>
            ) : (
              <Stack
                spacing={1.25}
                sx={{
                  flexShrink: 0,
                  alignItems: 'stretch',
                  width: 206,
                  maxWidth: '100%',
                }}
              >
                <InfoCard
                  icon={HighlightOff}
                  label={`Failed Logins Today: ${failedLoginsToday}`}
                  bgColor={theme.palette.semantic.surface.error}
                  borderColor={theme.palette.semantic.outline.error}
                  textColor={failedLoginsTextColor}
                />
                <InfoCard
                  icon={WarningAmber}
                  label={`Suspicious Logins: ${suspiciousBlocked7d}`}
                  bgColor={theme.palette.semantic.surface.amber}
                  borderColor={theme.palette.semantic.outline.amber}
                  textColor={theme.palette.semantic.metricText.warning}
                />
              </Stack>
            )}
          </Stack>
          {!isLoading && (
            <LoginActivityGraph
              dailyCounts={snapshot?.dailyCounts ?? []}
              sx={{
                width: '100%',
                ...(tier !== 'narrow' && { width: 'auto', flex: 1 }),
              }}
            />
          )}
        </Stack>
        <Stack
          sx={{
            width: '100%',
            ...(tier !== 'narrow' && { flexDirection: 'row' }),
            ...(tier === 'wide' && { width: 'auto', ...wideTileRegionSx }),
          }}
        >
          <SecondaryStat
            tier={tier}
            label="Leaked Passwords"
            value={isLoading ? <Skeleton width={40} /> : leakedPassword7d}
          />
          <SecondaryStat
            tier={tier}
            label="Suspicious Logins Blocked"
            value={isLoading ? <Skeleton width={40} /> : suspiciousBlocked7d}
          />
          <SecondaryStat
            tier={tier}
            label="Account Disabled Generic"
            value={isLoading ? <Skeleton width={40} /> : genericDisable7d}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
